import { Bloodwork } from './Bloodwork';

/**
 * Immune & Inflammatory Burden Index (I³) — pure scoring for the BWH Labs
 * screening questionnaire (Rountree/Strand). Questionnaire-driven, NOT
 * lab-marker driven: the practitioner enters one raw score per section (the
 * client's questionnaire subtotal) and we compute the weighted section scores,
 * the total Weighted I³ Score, the burden band, and the 4 Root Cause trigger
 * scores. No ranges / optimal bands are involved — this is plain arithmetic on
 * the entered values, kept pure so it is unit-tested independently and reused
 * by the React panels (same split as `resolveThyroidPattern` / `adrenalGauge`).
 *
 * Wording is intentionally observational / non-diagnostic — this is a screening
 * tool, not a diagnostic instrument.
 */

/** The 4-band burden level for the absolute Weighted I³ score (0–250). */
export type I3BurdenBand = 'low' | 'mild' | 'moderate' | 'high';

/** Zone for a 0–100 percentage, matching the LabsmartsGauge plotBands. */
export type I3Zone = 'green' | 'yellow' | 'orange' | 'red';

export interface I3SectionConfig {
  /** Bloodwork field id holding the raw section score. */
  id: keyof Bloodwork & string;
  /** Section number S1..S10. */
  num: number;
  /** Questionnaire wording (client-facing). */
  label: string;
  /** Number of questionnaire items in the section. */
  items: number;
  /** Maximum raw score (= items × 4). */
  max: number;
  /** Flowchart weight factor. */
  weight: number;
}

/**
 * The 10 sections. Labels use the questionnaire wording; weights and the
 * burden bands come from the practitioner flowchart. `max = items × 4` (each
 * item is scored 0–4).
 */
export const I3_SECTIONS: I3SectionConfig[] = [
  { id: 'i3EnergyFatigue', num: 1, label: 'Energy & Fatigue', items: 5, max: 20, weight: 1 },
  { id: 'i3Infections', num: 2, label: 'Frequent / Lingering Infections', items: 6, max: 24, weight: 1.25 },
  { id: 'i3InflammationPain', num: 3, label: 'Inflammation & Pain', items: 5, max: 20, weight: 1.25 },
  { id: 'i3GutDigestive', num: 4, label: 'Oral, Gut & Digestive Health', items: 7, max: 28, weight: 1.25 },
  { id: 'i3SkinRespiratory', num: 5, label: 'Skin, Eye & Respiratory Health', items: 7, max: 28, weight: 1.25 },
  { id: 'i3Musculoskeletal', num: 6, label: 'Musculoskeletal Health', items: 5, max: 20, weight: 1.25 },
  { id: 'i3NeuroImmune', num: 7, label: 'Neuro-Immune Resilience', items: 7, max: 28, weight: 0.75 },
  { id: 'i3Hormonal', num: 8, label: 'Hormonal & Endocrine', items: 7, max: 28, weight: 0.75 },
  { id: 'i3Sensitivities', num: 9, label: 'Sensitivities & Reactions', items: 4, max: 16, weight: 1.25 },
  { id: 'i3Biomarkers', num: 10, label: 'Immune & Inflammatory Biomarkers', items: 3, max: 12, weight: 1.5 },
];

/** Maximum possible raw total (Σ max) = 224. */
export const I3_RAW_MAX: number = I3_SECTIONS.reduce((s, x) => s + x.max, 0);
/** Maximum possible weighted total (Σ max × weight) = 250. */
export const I3_WEIGHTED_MAX: number = I3_SECTIONS.reduce((s, x) => s + x.max * x.weight, 0);

export interface I3SectionResult extends I3SectionConfig {
  /** Entered raw score, clamped to [0, max]; 0 when blank. */
  raw: number;
  /** Whether a value was actually entered (blank ≠ 0 for "not assessed" UI). */
  hasValue: boolean;
  /** raw ÷ max × 100 (0–100). */
  pct: number;
  /** raw × weight. */
  weighted: number;
  /** Zone of `pct`. */
  zone: I3Zone;
}

export interface I3Result {
  sections: I3SectionResult[];
  rawTotal: number;
  weightedTotal: number;
  weightedMax: number;
  band: I3BurdenBand;
  /** True when at least one of the 10 section scores was entered. */
  anyEntered: boolean;
}

export type RootCauseTriggerKey = 'microbes' | 'foods' | 'toxins' | 'trauma';
export type RootCauseToggleId = 'i3ToxinExposure' | 'i3TraumaHistory';

export interface RootCauseTriggerConfig {
  key: RootCauseTriggerKey;
  label: string;
  /** Primary (bolded on the flowchart) section numbers — counted 2×. */
  primary: number[];
  /** Secondary section numbers — counted 1×. */
  secondary: number[];
  /** Optional practitioner toggle that adds a boost when set. */
  toggleId?: RootCauseToggleId;
}

/**
 * The 4 Root Cause triggers and their "typical I³ sections" from the flowchart.
 * Toxins and Trauma intentionally share the same section set — they are pulled
 * apart only by their practitioner toggles.
 */
export const ROOT_CAUSE_TRIGGERS: RootCauseTriggerConfig[] = [
  { key: 'microbes', label: 'Microbes', primary: [1, 4, 7], secondary: [2, 3] },
  { key: 'foods', label: 'Foods', primary: [4, 5, 9], secondary: [1, 3] },
  { key: 'toxins', label: 'Toxins', primary: [1, 7, 8], secondary: [3, 4], toggleId: 'i3ToxinExposure' },
  { key: 'trauma', label: 'Trauma', primary: [1, 7, 8], secondary: [3, 4], toggleId: 'i3TraumaHistory' },
];

/**
 * Percentage-point boost added to the Toxins / Trauma baseline when the matching
 * practitioner toggle is set. ~one gauge band; tunable.
 */
export const ROOT_CAUSE_TOGGLE_BOOST = 15;

export interface RootCauseTriggerResult extends RootCauseTriggerConfig {
  /** Section blend before the toggle boost (0–100). */
  baseline: number;
  /** Final score after the toggle boost, clamped to [0, 100]. */
  score: number;
  zone: I3Zone;
  toggleApplied: boolean;
}

/** Map an absolute Weighted I³ score to its burden band. */
export function i3Band(weightedTotal: number): I3BurdenBand {
  if (weightedTotal >= 121) return 'high';
  if (weightedTotal >= 87) return 'moderate';
  if (weightedTotal >= 49) return 'mild';
  return 'low';
}

/** Zone for a 0–100 percentage — matches the gauge plotBands (50/70/90). */
export function pctZone(pct: number): I3Zone {
  if (pct >= 90) return 'red';
  if (pct >= 70) return 'orange';
  if (pct >= 50) return 'yellow';
  return 'green';
}

/** Read a numeric field, returning 0 for blank/non-numeric and whether it was set. */
function readNumber(bloodwork: Bloodwork | undefined, id: string): { value: number; hasValue: boolean } {
  const raw = bloodwork ? (bloodwork as any)[id] : undefined;
  if (raw === undefined || raw === null || raw === '') return { value: 0, hasValue: false };
  const n = Number(raw);
  if (isNaN(n)) return { value: 0, hasValue: false };
  return { value: n, hasValue: true };
}

/** Compute all per-section results plus the raw/weighted totals and band. */
export function computeI3(bloodwork: Bloodwork | undefined): I3Result {
  let anyEntered = false;
  const sections: I3SectionResult[] = I3_SECTIONS.map(cfg => {
    const { value, hasValue } = readNumber(bloodwork, cfg.id);
    if (hasValue) anyEntered = true;
    const raw = Math.min(Math.max(value, 0), cfg.max);
    const pct = cfg.max > 0 ? (raw / cfg.max) * 100 : 0;
    return {
      ...cfg,
      raw,
      hasValue,
      pct,
      weighted: raw * cfg.weight,
      zone: pctZone(pct),
    };
  });

  const rawTotal = sections.reduce((s, x) => s + x.raw, 0);
  const weightedTotal = sections.reduce((s, x) => s + x.weighted, 0);

  return {
    sections,
    rawTotal,
    weightedTotal,
    weightedMax: I3_WEIGHTED_MAX,
    band: i3Band(weightedTotal),
    anyEntered,
  };
}

/** Compute the 4 Root Cause trigger scores from the section percentages. */
export function computeRootCauseTriggers(bloodwork: Bloodwork | undefined): RootCauseTriggerResult[] {
  const { sections } = computeI3(bloodwork);
  const pctByNum = new Map<number, number>(sections.map(s => [s.num, s.pct]));

  return ROOT_CAUSE_TRIGGERS.map(cfg => {
    const sumPrimary = cfg.primary.reduce((s, n) => s + (pctByNum.get(n) ?? 0), 0);
    const sumSecondary = cfg.secondary.reduce((s, n) => s + (pctByNum.get(n) ?? 0), 0);
    const denom = 2 * cfg.primary.length + cfg.secondary.length;
    const baseline = denom > 0 ? (2 * sumPrimary + sumSecondary) / denom : 0;

    const toggleApplied =
      !!cfg.toggleId && readNumber(bloodwork, cfg.toggleId).value > 0;
    const score = Math.min(100, baseline + (toggleApplied ? ROOT_CAUSE_TOGGLE_BOOST : 0));

    return {
      ...cfg,
      baseline,
      score,
      zone: pctZone(score),
      toggleApplied,
    };
  });
}
